import {
  Button,
  Container,
  Divider,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { NotificationPreferences } from "@features/notifications";
import { usePasswordForm, useProfileForm } from "../hooks/useSettingsForm";

function ProfileSection() {
  const { t } = useTranslation();
  const form = useProfileForm();

  return (
    <Paper withBorder p="lg" radius="md">
      <Title order={3} mb="md">
        {t("nav.profile")}
      </Title>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.submit();
        }}
      >
        <Stack gap="md">
          <TextInput
            label={t("auth.name")}
            placeholder={t("auth.namePlaceholder")}
            value={form.values.name}
            onChange={(e) => form.handleChange("name", e.currentTarget.value)}
            error={form.errors.name}
            data-testid="settings-name-input"
          />

          <TextInput
            label={t("auth.email")}
            placeholder={t("auth.emailPlaceholder")}
            value={form.values.email}
            onChange={(e) => form.handleChange("email", e.currentTarget.value)}
            error={form.errors.email}
            type="email"
            data-testid="settings-email-input"
          />

          <Select
            label={t("auth.preferredLanguage")}
            value={form.values.preferredLanguage}
            onChange={(v) => form.handleChange("preferredLanguage", v ?? "en")}
            data={[
              { value: "en", label: t("auth.preferredLanguageEn") },
              { value: "es", label: t("auth.preferredLanguageEs") },
            ]}
            data-testid="settings-language-select"
          />

          <Button
            type="submit"
            loading={form.isPending}
            data-testid="settings-profile-submit"
          >
            {t("common.save")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}

function SecuritySection() {
  const { t } = useTranslation();
  const form = usePasswordForm();

  return (
    <Paper withBorder p="lg" radius="md">
      <Title order={3} mb="md">
        {t("auth.security")}
      </Title>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.submit();
        }}
      >
        <Stack gap="md">
          <PasswordInput
            label={t("auth.currentPassword")}
            placeholder={t("auth.currentPasswordPlaceholder")}
            value={form.values.currentPassword}
            onChange={(e) =>
              form.handleChange("currentPassword", e.currentTarget.value)
            }
            error={form.errors.currentPassword}
            data-testid="settings-current-password-input"
          />

          <PasswordInput
            label={t("auth.newPassword")}
            placeholder={t("auth.newPasswordPlaceholder")}
            value={form.values.newPassword}
            onChange={(e) =>
              form.handleChange("newPassword", e.currentTarget.value)
            }
            error={form.errors.newPassword}
            data-testid="settings-new-password-input"
          />

          <PasswordInput
            label={t("auth.confirmPassword")}
            placeholder={t("auth.confirmPasswordPlaceholder")}
            value={form.values.confirmPassword}
            onChange={(e) =>
              form.handleChange("confirmPassword", e.currentTarget.value)
            }
            error={form.errors.confirmPassword}
            data-testid="settings-confirm-password-input"
          />

          <Button
            type="submit"
            loading={form.isPending}
            data-testid="settings-password-submit"
          >
            {t("auth.changePassword")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}

export function SettingsForm() {
  const { t } = useTranslation();

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>{t("nav.settings")}</Title>
          <Text c="dimmed" size="sm" mt="xs">
            {t("auth.settingsDescription")}
          </Text>
        </div>

        <ProfileSection />

        <Divider />

        <NotificationPreferences />

        <Divider />

        <SecuritySection />
      </Stack>
    </Container>
  );
}
